import type { IncidentStatus } from '../../types/canvision.types';

// ---------------------------------------------------------------------------
// Action descriptor returned by getActionsForStatus()
// ---------------------------------------------------------------------------

export interface IncidentActionDescriptor {
  /** Button label */
  label: string;
  /** Target status (null for actions that don't change status, e.g. "View") */
  action: IncidentStatus | null;
  /** MUI color */
  color: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'inherit' | 'info';
  /** MUI button variant */
  variant: 'contained' | 'outlined' | 'text';
  /** Icon identifier — kept as a string so this file stays free of JSX imports.
   *  The consuming component maps the key to the actual MUI icon element. */
  icon: string;
}

// ---------------------------------------------------------------------------
// Status → actions mapping  (6-state model)
//
//  new        → Start Review
//  reviewing  → Dispatch Guard (opens dialog), Dismiss
//  dispatched → Resolve
//  resolved   → Close
//  closed     → (terminal — no actions)
//  dismissed  → (terminal — no actions)
// ---------------------------------------------------------------------------

const ACTION_MAP: Record<string, IncidentActionDescriptor[]> = {
  new: [
    { label: 'View', action: null, color: 'primary', variant: 'outlined', icon: 'Visibility' },
    { label: 'Start Review', action: 'reviewing', color: 'primary', variant: 'contained', icon: 'PlayArrow' },
  ],
  reviewing: [
    { label: 'View', action: null, color: 'primary', variant: 'outlined', icon: 'Visibility' },
    { label: 'Dispatch Guard', action: 'dispatched', color: 'secondary', variant: 'contained', icon: 'Send' },
    { label: 'Dismiss', action: 'dismissed', color: 'inherit', variant: 'outlined', icon: 'Close' },
  ],
  dispatched: [
    { label: 'View', action: null, color: 'primary', variant: 'outlined', icon: 'Visibility' },
    { label: 'Resolve', action: 'resolved', color: 'success', variant: 'contained', icon: 'CheckCircle' },
  ],
  resolved: [
    { label: 'View', action: null, color: 'primary', variant: 'outlined', icon: 'Visibility' },
    { label: 'Close', action: 'closed', color: 'inherit', variant: 'outlined', icon: 'Archive' },
  ],
  closed: [],
  dismissed: [],
};

// ---------------------------------------------------------------------------
// Public helper
// ---------------------------------------------------------------------------

/**
 * Return the list of available action descriptors for a given incident status.
 *
 * The `icon` field is a string key (e.g. `"Visibility"`, `"PlayArrow"`) that
 * the consuming component should map to the corresponding MUI icon element.
 */
export function getActionsForStatus(status: IncidentStatus | string): IncidentActionDescriptor[] {
  return ACTION_MAP[status] ?? [];
}
